"use client";

import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

import bedImg from "@/app/assets/illustration/blog/beds.webp";
import flaskImg from "@/app/assets/illustration/blog/flask.png";

interface BlogPromotionSectionProps {
    className?: string;
}

export const BlogPromotionSection = ({ className = "" }: BlogPromotionSectionProps) => {
    const t = useTranslations("BlogPromotion");
    const locale = useLocale();

    const blogArticles = [
        {
            id: "room_sharing",
            image: bedImg,
            slug: "room_sharing",
        },
        {
            id: "wake_windows",
            image: flaskImg,
            url: "/gated/wake_windows",
        },
    ];

    return (
        <section className={`py-12 ${className}`}>
            <div className="container mx-auto px-4">
                <div className="text-center mb-10 max-w-3xl mx-auto">
                    <h2 className="text-4xl font-bold mb-6">{t("title")}</h2>
                    <p className="text-lg mb-8">{t("description")}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {blogArticles.map((article) => {
                        const articleT = t.raw(`articles.${article.id}`);
                        let href = "";
                        if (article.slug) {
                            href = `/${locale}/blog/${article.slug}`;
                        } else if (article.url) {
                            href = `/${locale}${article.url}`;
                        }

                        return (
                            <Link className="group h-full" href={href} key={article.id}>
                                <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                                    <div className="relative h-48 w-full overflow-hidden flex-shrink-0">
                                        <Image
                                            alt={articleT.title || ""}
                                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                                            fill
                                            src={article.image}
                                        />
                                    </div>
                                    <div className="p-6 flex-grow flex flex-col">
                                        <h3 className="text-xl font-semibold mb-3 text-primary-900 group-hover:text-primary-100 transition-colors duration-300">
                                            {articleT.title}
                                        </h3>
                                        <p className="text-gray-600 mb-4 line-clamp-2 flex-grow">{articleT.excerpt}</p>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="text-center mt-8 max-w-3xl mx-auto">
                    <Link
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-lg font-medium rounded-full !text-white bg-primary-100 hover:bg-primary-200 transition-colors"
                        href={`/${locale}/blog`}
                    >
                        {t("viewAll")}
                    </Link>
                </div>
            </div>
        </section>
    );
};
